import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { loyaltyApi, type LoyaltyPosCode } from '../services/loyaltyApi';

type Props = {
  token: string;
  firstName: string;
  lastName: string | null;
  phone?: string | null;
  bonuses?: number;
};

function formatPhone(phone: string): string {
  const d = phone.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('7')) {
    return `+7 ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9)}`;
  }
  return phone;
}

export function LoyaltyCard({ token, firstName, lastName, phone }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState<LoyaltyPosCode | null>(null);
  const [remainSec, setRemainSec] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);

  const displayName = [firstName, lastName].filter(Boolean).join(' ') || firstName;

  useEffect(() => {
    let cancelled = false;
    loyaltyApi
      .getPolicy()
      .then((p) => {
        if (!cancelled) setDiscountPercent(p.discountPercent);
      })
      .catch(() => {
        if (!cancelled) setDiscountPercent(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const issue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await loyaltyApi.issuePosCode(token);
      setIssued(data);
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось получить код');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!open) {
      setIssued(null);
      setError('');
      return;
    }
    void issue();
  }, [open, issue]);

  useEffect(() => {
    if (!open || !issued) return;
    const tick = () => {
      const left = Math.max(0, Math.ceil((new Date(issued.expiresAt).getTime() - Date.now()) / 1000));
      setRemainSec(left);
      if (left <= 0 && !loading) void issue();
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [open, issued, issue, loading]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-left mb-6 rounded-3xl overflow-hidden active:scale-[0.99] transition-transform"
        style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 55%, #1c1914 100%)',
          border: '1px solid rgba(212,175,55,0.35)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        }}
      >
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start justify-between gap-3">
            <p
              className="text-[10px] tracking-[0.28em] uppercase"
              style={{ color: 'rgba(212,175,55,0.85)', fontFamily: 'Unbounded, sans-serif' }}
            >
              Theory
            </p>
            <p className="text-[10px] tracking-[0.16em] uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Карта лояльности
            </p>
          </div>
          <p
            className="mt-8 text-xl font-semibold truncate"
            style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.04em' }}
          >
            {displayName}
          </p>
          {phone ? (
            <p
              className="mt-3 text-lg font-semibold tracking-wide"
              style={{ fontFamily: 'Unbounded, sans-serif', color: '#fff' }}
            >
              {formatPhone(phone)}
            </p>
          ) : null}
          <div className="mt-5 flex items-end justify-between">
            <div>
              <p className="text-[10px] tracking-[0.14em] uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {discountPercent > 0 ? 'Скидка' : 'Карта гостя'}
              </p>
              <p className="text-lg font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#e8c872' }}>
                {discountPercent > 0 ? `${discountPercent}%` : '—'}
              </p>
            </div>
            <p className="text-[11px] text-right" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {phone ? 'Назовите номер на кассе' : 'Нажмите, чтобы показать код'}
            </p>
          </div>
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={() => !loading && setOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-3xl p-5"
            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-white">Код для кассы</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2"
                style={{ color: 'rgba(255,255,255,0.4)' }}
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Назовите эти цифры кассиру
            </p>
            {loading && !issued ? (
              <div className="flex justify-center py-10">
                <Loader2 className="animate-spin" size={28} style={{ color: 'rgba(255,255,255,0.3)' }} />
              </div>
            ) : error && !issued ? (
              <p className="text-sm text-red-400 mb-4">{error}</p>
            ) : (
              <>
                <p
                  className="text-center font-bold tracking-[0.35em] mb-3"
                  style={{ fontFamily: 'Unbounded, sans-serif', fontSize: 44, color: '#fff' }}
                >
                  {issued?.code ?? '————'}
                </p>
                <p className="text-center text-sm mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Обновится через {remainSec} с
                </p>
                {error && <p className="text-center text-xs text-red-400 mt-2">{error}</p>}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
